import type { ContactPoint, HumanName, Patient } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';

import {
  CodeSelectInput,
  ConceptSelectInput,
  DateInput,
  StringInput,
} from '../../components/inputs';
import { useFhirEditState } from '../../core/FhirEditScope';
import type { FhirView } from '../../core/view-contract';
import { formatHumanName } from '../accessors';

import {
  CONTACT_RELATIONSHIP_OPTIONS,
  ETHNICITY_OPTIONS,
  GENDER_IDENTITY_OPTIONS,
  INDIVIDUAL_PRONOUNS_URL,
  LANGUAGE_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  PRONOUN_OPTIONS,
  RACE_OPTIONS,
  US_CORE_ETHNICITY_URL,
  US_CORE_GENDER_IDENTITY_URL,
  US_CORE_RACE_URL,
  preferredLanguageIndex,
  withConceptExtension,
  withContact,
  withOmbExtension,
  withPreferredLanguage,
} from './us-core';

/**
 * Demographics edit variant. Two field groups mirroring the profile rail:
 * identity & demographics, contact & relationships. All edits go through
 * the edit scope's store (immutable copies of the resource); the app's
 * save bar commits them. Given/family name and date of birth are required —
 * the app's save action should gate on `isPatientEditValid`.
 */

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
] as const;

type Gender = NonNullable<Patient['gender']>;

/** The name being edited: the official name when present, else the first. */
function editableNameIndex(patient: Patient): number {
  const index =
    patient.name?.findIndex((name) => name.use === 'official') ?? -1;
  return index >= 0 ? index : 0;
}

function editableName(patient: Patient): HumanName | undefined {
  return patient.name?.[editableNameIndex(patient)];
}

/** Required-field gate for the app's save action: given + family + DOB. */
export function isPatientEditValid(patient: Patient): boolean {
  const name = editableName(patient);
  return Boolean(
    name?.given?.join(' ').trim() && name?.family?.trim() && patient.birthDate,
  );
}

function withName(patient: Patient, update: Partial<HumanName>): Patient {
  const names = [...(patient.name ?? [{}])];
  const index = editableNameIndex(patient);
  names[index] = { ...names[index], ...update };
  return { ...patient, name: names };
}

function withTelecom(
  patient: Patient,
  system: 'phone' | 'email',
  value: string | undefined,
): Patient {
  const telecom = [...(patient.telecom ?? [])];
  const index = telecom.findIndex((point) => point.system === system);
  if (value === undefined) {
    if (index >= 0) telecom.splice(index, 1);
  } else {
    const next: ContactPoint = { system, value };
    if (index >= 0) telecom[index] = { ...telecom[index], value };
    else telecom.push(next);
  }
  return { ...patient, telecom: telecom.length > 0 ? telecom : undefined };
}

function withAddress(
  patient: Patient,
  update: Partial<{
    line: string | undefined;
    city: string | undefined;
    state: string | undefined;
    postalCode: string | undefined;
  }>,
): Patient {
  const address = { ...(patient.address?.[0] ?? {}) };
  if ('line' in update) {
    address.line = update.line ? [update.line] : undefined;
  }
  if ('city' in update) address.city = update.city;
  if ('state' in update) address.state = update.state;
  if ('postalCode' in update) address.postalCode = update.postalCode;
  return {
    ...patient,
    address: [address, ...(patient.address?.slice(1) ?? [])],
  };
}

function conceptExtension(patient: Patient, url: string) {
  return patient.extension?.find((extension) => extension.url === url)
    ?.valueCodeableConcept;
}

function ombConcept(patient: Patient, url: string) {
  const omb = patient.extension
    ?.find((extension) => extension.url === url)
    ?.extension?.find((nested) => nested.url === 'ombCategory')?.valueCoding;
  return omb ? { coding: [omb], text: omb.display } : undefined;
}

function ombOption(
  options: typeof RACE_OPTIONS,
  code: string | undefined,
): (typeof RACE_OPTIONS)[number] | undefined {
  return options.find((option) => option.code === code);
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Text size="sm" weight="semibold" color="secondary">
      {children}
    </Text>
  );
}

export const PatientEditView: FhirView<Patient> = ({ data, ctx }) => {
  const { current, setCurrent, dirty } = useFhirEditState(data);
  const name = editableName(current);
  const phone = current.telecom?.find((p) => p.system === 'phone')?.value;
  const email = current.telecom?.find((p) => p.system === 'email')?.value;
  const address = current.address?.[0];
  const language =
    current.communication?.[preferredLanguageIndex(current)]?.language;
  const contact = current.contact?.[0];
  const contactPhone = contact?.telecom?.find(
    (point) => point.system === 'phone',
  )?.value;

  const form = (
    <Stack direction="vertical" gap={5} className="w-full">
      <Stack direction="vertical" gap={3} className="w-full">
        <SectionHeading>Identity &amp; demographics</SectionHeading>
        <Grid columns={2} className="gap-x-4 gap-y-3">
          <StringInput
            label="Given name"
            required
            value={name?.given?.join(' ')}
            onChange={(next) =>
              setCurrent(
                withName(current, {
                  given: next ? next.split(' ') : undefined,
                }),
              )
            }
          />
          <StringInput
            label="Family name"
            required
            value={name?.family}
            onChange={(next) => setCurrent(withName(current, { family: next }))}
          />
          <DateInput
            label="Date of birth"
            required
            value={current.birthDate}
            onChange={(next) => setCurrent({ ...current, birthDate: next })}
          />
          <CodeSelectInput
            label="Legal sex"
            value={current.gender as Gender | undefined}
            options={GENDER_OPTIONS}
            onChange={(next) => setCurrent({ ...current, gender: next })}
          />
          <ConceptSelectInput
            label="Gender identity"
            value={conceptExtension(current, US_CORE_GENDER_IDENTITY_URL)}
            options={GENDER_IDENTITY_OPTIONS}
            onChange={(next) =>
              setCurrent(
                withConceptExtension(
                  current,
                  US_CORE_GENDER_IDENTITY_URL,
                  next,
                ),
              )
            }
          />
          <ConceptSelectInput
            label="Pronouns"
            value={conceptExtension(current, INDIVIDUAL_PRONOUNS_URL)}
            options={PRONOUN_OPTIONS}
            onChange={(next) =>
              setCurrent(
                withConceptExtension(current, INDIVIDUAL_PRONOUNS_URL, next),
              )
            }
          />
          <ConceptSelectInput
            label="Race"
            value={ombConcept(current, US_CORE_RACE_URL)}
            options={RACE_OPTIONS}
            onChange={(next) =>
              setCurrent(
                withOmbExtension(
                  current,
                  US_CORE_RACE_URL,
                  ombOption(RACE_OPTIONS, next?.coding?.[0]?.code),
                ),
              )
            }
          />
          <ConceptSelectInput
            label="Ethnicity"
            value={ombConcept(current, US_CORE_ETHNICITY_URL)}
            options={ETHNICITY_OPTIONS}
            onChange={(next) =>
              setCurrent(
                withOmbExtension(
                  current,
                  US_CORE_ETHNICITY_URL,
                  ombOption(ETHNICITY_OPTIONS, next?.coding?.[0]?.code),
                ),
              )
            }
          />
          <ConceptSelectInput
            label="Preferred language"
            value={language}
            options={LANGUAGE_OPTIONS}
            onChange={(next) =>
              setCurrent(withPreferredLanguage(current, next))
            }
          />
          <ConceptSelectInput
            label="Marital status"
            value={current.maritalStatus}
            options={MARITAL_STATUS_OPTIONS}
            onChange={(next) => setCurrent({ ...current, maritalStatus: next })}
          />
        </Grid>
      </Stack>

      <Stack direction="vertical" gap={3} className="w-full">
        <SectionHeading>Contact &amp; relationships</SectionHeading>
        <Grid columns={2} className="gap-x-4 gap-y-3">
          <StringInput
            label="Phone"
            type="tel"
            value={phone}
            onChange={(next) => setCurrent(withTelecom(current, 'phone', next))}
          />
          <StringInput
            label="Email"
            type="email"
            value={email}
            onChange={(next) => setCurrent(withTelecom(current, 'email', next))}
          />
          <div className="col-span-2">
            <StringInput
              label="Street address"
              value={address?.line?.join(' ')}
              onChange={(next) =>
                setCurrent(withAddress(current, { line: next }))
              }
            />
          </div>
          <StringInput
            label="City"
            value={address?.city}
            onChange={(next) =>
              setCurrent(withAddress(current, { city: next }))
            }
          />
          <Grid columns={2} className="gap-x-4">
            <StringInput
              label="State"
              value={address?.state}
              onChange={(next) =>
                setCurrent(withAddress(current, { state: next }))
              }
            />
            <StringInput
              label="Postal code"
              value={address?.postalCode}
              onChange={(next) =>
                setCurrent(withAddress(current, { postalCode: next }))
              }
            />
          </Grid>
          <StringInput
            label="Emergency contact"
            value={contact?.name?.text ?? formatHumanName(contact?.name)}
            onChange={(next) =>
              setCurrent(withContact(current, { name: next }))
            }
          />
          <ConceptSelectInput
            label="Relationship"
            value={contact?.relationship?.[0]}
            options={CONTACT_RELATIONSHIP_OPTIONS}
            onChange={(next) =>
              setCurrent(withContact(current, { relationship: next }))
            }
          />
          <StringInput
            label="Emergency contact phone"
            type="tel"
            value={contactPhone}
            onChange={(next) =>
              setCurrent(withContact(current, { phone: next }))
            }
          />
        </Grid>
      </Stack>
    </Stack>
  );

  if (ctx.placement === 'modal') {
    return <div data-view="patient-edit">{form}</div>;
  }

  return (
    <div
      data-view="patient-edit"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold">
          Demographics
        </Text>
        {dirty && <Tag pill subtle size="xs" color="warning" label="Edited" />}
      </Stack>
      <div className="mt-3">{form}</div>
    </div>
  );
};
